/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
// Third-party
import { css, keyframes } from '@emotion/react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { App, Modal, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
// Relative
import Icon from 'src/components/Icon'
import ConfigurePage from './ConfigurePage'
import ConstraintsSection from './components/ConstraintsSection'
import DivisionTeamsSection from './components/DivisionTeamsSection'
import GeneratedGamesSection from './components/GeneratedGamesSection'
import type { GeneratedGame } from './components/GeneratedGamesSection'
import GenerateScheduleModal from './components/GenerateScheduleModal'
import type { Division } from './components/GenerateScheduleModal'
import SchedulerErrorBoundary from './components/SchedulerErrorBoundary'
import ScheduleFilters, {
  DEFAULT_FILTER_VALUES,
  type ScheduleFilterValues,
} from './components/ScheduleFilters'
import ScheduleReviewTable from './components/ScheduleReviewTable'
import type { DivisionRow } from './components/ScheduleReviewTable'
// import ScheduleChartsSection from './components/ScheduleChartsSection'
import ScheduleSummary from './components/ScheduleSummary'
import SchedulerNavigationBar, {
  NavigationTab,
} from './components/SchedulerNavigationBar'
import { useSchedulerCompetition } from './hooks/useSchedulerCompetition'
import type { Competition } from './hooks/useSchedulerCompetition'
import { useSchedulerConstraints } from './hooks/useSchedulerConstraints'
import type { SchedulerConstraint } from './hooks/useSchedulerConstraints'
import { useSchedulerDivisions } from './hooks/useSchedulerDivisions'
import type { DivisionItem } from './hooks/useSchedulerDivisions'
import { useSchedulerLocations } from './hooks/useSchedulerLocations'
import type { Venue } from './hooks/useSchedulerLocations'
import { useGameEdit } from './hooks/useGameEdit'
import type { GameEditData } from './hooks/useGameEdit'
import { useGameMutation } from './hooks/useGameMutation'
import PreflightValidationModal from './components/PreflightValidationModal'
import { useSchedulerRequest } from './hooks/useSchedulerRequest'
import type { DivisionJob } from './hooks/useSchedulerRequest'
import CuratingState from './components/CuratingState'
import PartiallyGeneratedState from './components/PartiallyGeneratedState'
import CancelConfirmModal from './components/CancelConfirmModal'
import PublishScheduleModal from './components/PublishScheduleModal'
import { useSchedulerTimeSlots } from './hooks/useSchedulerTimeSlots'
import type { TimeSlot } from './hooks/useSchedulerTimeSlots'
import { generateGamesCsv, downloadCsv } from './utils/csvExport'
import { useCheckpoint } from './hooks/useCheckpoint'
import {
  transformGamesToDisplay,
  transformGamesToDivisionRows,
  getScoreLabel,
} from './utils/transformGames'
import { BLACKOUT_BYE_CONSTRAINT_CODES } from './constants'

interface RouteParams {
  competitionId: string
  requestId?: string
}

// ─── Constants ────────────────────────────────────────────────
const DEFAULT_BUFFER_MINUTES = 15
const DEFAULT_GAMES_PER_MATCHUP = 1
const MAX_SOLVER_TIME_SECONDS = 300
const DEFAULT_OPTIMIZATION_LEVEL = 'balanced'
const MIN_TEAMS_PER_DIVISION = 2
const SUCCESS_ANIMATION_DURATION_MS = 1500

function buildGeneratePayload(
  competition: Competition,
  divisions: DivisionItem[],
  venues: Venue[],
  timeSlots: TimeSlot[],
  constraints: SchedulerConstraint[],
  selectedDivisionIds: string[],
  selectedLocationIds: string[],
  existingRequestId?: string | null,
  divisionPriority?: string[]
) {
  const filteredDivisions = divisions.filter((d) =>
    selectedDivisionIds.includes(d.id)
  )

  const filteredVenues =
    selectedLocationIds.length > 0
      ? venues.filter((v) => selectedLocationIds.includes(v.id))
      : venues

  const facilityIds = new Set(
    filteredVenues.flatMap((v) => v.facilities.map((f) => f.id))
  )

  const expandedTimeSlots = timeSlots
    .filter((ts) => ts.is_active && facilityIds.has(ts.facility_id))
    .flatMap((ts) =>
      ts.days.flatMap((day) =>
        ts.time_ranges.map((tr) => ({
          facility_uid: ts.facility_id,
          day_of_week: day,
          start_time: tr.start_time,
          end_time: tr.end_time,
          game_duration_minutes: competition.regulation_game_length ?? 60,
          buffer_minutes: DEFAULT_BUFFER_MINUTES,
          is_preferred: false,
        }))
      )
    )

  // Build sets of valid division/team UIDs for filtering stale references
  const validDivisionUids = new Set(filteredDivisions.map((d) => d.id))
  const validTeamUids = new Set(
    filteredDivisions.flatMap((d) => d.teams.map((t) => t.id))
  )

  const mappedConstraints = constraints
    .filter((c) => c.is_enabled)
    .map((c) => {
      // Filter out stale division/team UIDs that no longer exist
      const filteredDivisionUids = c.division_uids
        ? c.division_uids.filter((uid) => validDivisionUids.has(uid))
        : null
      const filteredTeamUids = c.team_uids
        ? c.team_uids.filter((uid) => validTeamUids.has(uid))
        : null

      return {
        type: c.type.code,
        parameters: c.parameters,
        is_hard: c.is_hard,
        priority: c.priority,
        weight: c.weight,
        division_uids:
          filteredDivisionUids && filteredDivisionUids.length > 0
            ? filteredDivisionUids
            : null,
        team_uids:
          filteredTeamUids && filteredTeamUids.length > 0
            ? filteredTeamUids
            : null,
      }
    })

  // Check URL param for debug mode
  const urlParams = new URLSearchParams(window.location.search)
  const isDryRun = urlParams.get('debug_scheduler') === '1'

  return {
    schedule: {
      uid: competition.id,
      starts_at: dayjs.utc(competition.starts_at).format('YYYY-MM-DD'),
      ends_at: dayjs.utc(competition.ends_at).format('YYYY-MM-DD'),
    },
    divisions: filteredDivisions.map((d) => ({
      uid: d.id,
      name: d.name,
      teams: d.teams.map((t) => ({
        uid: t.id,
        name: t.name,
        home_venue_uid: null,
      })),
      games_per_matchup: DEFAULT_GAMES_PER_MATCHUP,
      games_per_team_per_week: null,
    })),
    venues: filteredVenues.map((v) => ({
      uid: v.id,
      name: v.name,
      facilities: v.facilities.map((f) => ({
        uid: f.id,
        name: f.name,
      })),
    })),
    time_slots: expandedTimeSlots,
    constraints: mappedConstraints,
    options: {
      max_time_seconds: MAX_SOLVER_TIME_SECONDS,
      optimization_level: DEFAULT_OPTIMIZATION_LEVEL,
      fill_day_first: true,
      ...(divisionPriority && divisionPriority.length > 0
        ? { division_priority: divisionPriority }
        : {}),
    },
    dry_run: isDryRun,
    ...(existingRequestId ? { scheduler_request_id: existingRequestId } : {}),
  }
}

// Success animation keyframes
const successFadeIn = keyframes`
  0% { opacity: 0; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
`

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`

/**
 * Sort division jobs by the user-specified dispatch order.
 * Falls back to original order if no priority is set.
 */
function sortJobsByPriority(
  jobs: DivisionJob[],
  dispatchOrder: string[] | undefined
): DivisionJob[] {
  if (!dispatchOrder || dispatchOrder.length === 0) {
    return jobs
  }
  const orderMap = new Map(dispatchOrder.map((id, idx) => [id, idx]))
  return [...jobs].sort((a, b) => {
    const ai = orderMap.get(a.division_id) ?? Number.MAX_SAFE_INTEGER
    const bi = orderMap.get(b.division_id) ?? Number.MAX_SAFE_INTEGER
    return ai - bi
  })
}

const checkmarkPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`

const LOADING_SPINNER_STYLE = { fontSize: 36, color: colors.PRIMARY_300 }

// ─── Static styles (module-level, computed once) ──────────────
const pageContainerStyles = css`
  min-height: 100vh;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const headerStyles = css`
  background-color: ${colors.SECONDARY_400};
  padding: 40px 88px;
`

const headerInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const leftSectionStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyles = css`
  color: ${colors.NEUTRAL_50};
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.44px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.WHITE};
  }
`

const titleSectionStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const titleStyles = css`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 48px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin: 0;
  line-height: 48px;
`

const actionButtonsStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const cancelButtonStyles = css`
  padding: 8px 16px;
  background-color: transparent;
  border: 2px solid ${colors.ERROR_200};
  color: ${colors.ERROR_200};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 23.2px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.ERROR_200};
    color: ${colors.WHITE};
  }
`

const calendarViewButtonStyles = css`
  padding: 8px 16px;
  background-color: transparent;
  border: 2px solid ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 23.2px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.PRIMARY_300}22;
  }
`

const publishButtonStyles = css`
  padding: 8px 16px;
  background-color: ${colors.PRIMARY_300};
  border: none;
  color: ${colors.SECONDARY_300};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 23.2px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${colors.PRIMARY_200};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const generateButtonStyles = css`
  padding: 8px;
  background-color: ${colors.PRIMARY_300};
  border: none;
  color: ${colors.SECONDARY_300};
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 23.2px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${colors.PRIMARY_200};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const contentContainerStyles = css`
  flex: 1;
  padding: 40px 88px;
  background-color: ${colors.SECONDARY_500};
`

const contentInnerStyles = css`
  max-width: 1440px;
  margin: 0 auto;
`

/**
 * Scheduler Wizard Main Page
 */
const SchedulerWizard: React.FC = () => {
  const history = useHistory()
  const { competitionId, requestId: urlRequestId } = useParams<RouteParams>()
  const { message, modal } = App.useApp()
  const [activeNavTab, setActiveNavTab] = useState('time-slots')

  // Navigation tabs configuration — computed dynamically after all hooks below

  // Fetch competition data
  const {
    competition,
    refetchCompetition,
    featureFlags,
    loading: competitionLoading,
  } = useSchedulerCompetition(competitionId)

  // Feature flag guard — redirect if scheduler-wizard is not enabled
  useEffect(() => {
    // Wait for competition data (and meta.feature_flags) to load
    if (competitionLoading || !competition) {
      return
    }
    // Redirect unless scheduler-wizard is explicitly enabled
    if (featureFlags?.['scheduler-wizard'] !== true) {
      history.replace(`${ROUTES.SCHEDULE_ROOT}/${competitionId}/schedule`)
    }
  }, [competition, featureFlags, competitionLoading, competitionId, history])

  // Manage scheduler request state
  const {
    request: schedulerRequest,
    requestId,
    loading: requestLoading,
    isResuming,
    createRequest,
    isPolling,
    isTimedOut,
    timeoutReason,
    startPolling,
    keepWaiting,
    fetchRequest,
    resetDivisions,
    cancelRequest,
    retryRequest,
    retryFailedDivisions,
    updateGameStatus,
    updateBulkGameStatus,
    updateDivisionApprovalStatus,
    updateAllGamesApproved,
  } = useSchedulerRequest(urlRequestId)

  // Fetch divisions data
  const {
    divisions,
    loading: divisionsLoading,
    error: divisionsError,
    toggleExpanded,
    addTeam,
    addDivision,
    deleteTeam,
    deleteDivision,
  } = useSchedulerDivisions(
    competitionId,
    schedulerRequest?.approved_division_uids
  )

  // Fetch locations (venues) - lifted to parent so data persists across tab switches
  const {
    venues,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useSchedulerLocations(competitionId)

  // Fetch time slots - lifted to parent so data persists across tab switches
  const {
    timeSlots,
    timeSlotCounts,
    loading: timeSlotsLoading,
    createTimeSlot,
    updateTimeSlot,
    deleteTimeSlot,
    duplicateTimeSlot,
    getTimeSlotsForFacility,
    createTimeSlotForVenue,
    updateTimeSlotBatch,
    deleteTimeSlotBatch,
  } = useSchedulerTimeSlots(competitionId)

  // Fetch constraints - lifted to parent, shared between Time Slots and Constraints tabs
  const {
    constraints,
    blackoutDates: rawBlackoutDates,
    loading: constraintsLoading,
    addBlackoutDate,
    deleteConstraint,
    toggleEnabled,
    updateConstraint,
    updateConstraintType,
    reorderConstraints,
    addConstraint,
    refetch: refetchConstraints,
  } = useSchedulerConstraints(requestId)

  // Game editing hook
  const {
    editingGameId,
    saving: gameSaving,
    approving: gameApproving,
    error: gameEditError,
    lastEvaluation,
    startEditing,
    cancelEditing,
    approveGame,
    unapproveGame,
    approveAllGames,
    approveByDivisions,
    bulkApproveGames,
    bulkRevertGames,
    revertByDivisions,
    clearError: clearGameEditError,
  } = useGameEdit()

  // Game mutation (pre-flight validation) hook
  const refreshAfterMutation = useCallback(() => {
    if (schedulerRequest?.id) {
      fetchRequest(schedulerRequest.id)
    }
  }, [schedulerRequest?.id, fetchRequest])

  const {
    preview: mutationPreview,
    modalVisible: mutationModalVisible,
    loading: mutationLoading,
    confirming: mutationConfirming,
    error: mutationError,
    requestPreview: requestMutationPreview,
    requestSwapPreview,
    confirmMutation,
    selectAlternativeSlot,
    cancelMutation,
  } = useGameMutation(refreshAfterMutation)

  const { isSaving, isRestoring, saveCheckpoint, restoreCheckpoint } =
    useCheckpoint()

  const handleSwapGamesPreview = useCallback(
    (gameAId: string, gameBId: string) => {
      if (!schedulerRequest?.id) {
        return
      }
      requestSwapPreview(schedulerRequest.id, gameAId, gameBId)
    },
    [schedulerRequest?.id, requestSwapPreview]
  )

  // Map divisions to constraint-compatible format
  const constraintDivisions = useMemo(
    () => divisions.map((d) => ({ uid: d.id, name: d.name })),
    [divisions]
  )

  // Extract teams from divisions for constraints
  const constraintTeams = useMemo(
    () =>
      divisions.flatMap((d) =>
        d.teams.map((t) => ({ uid: t.id, name: t.name, divisionUid: d.id }))
      ),
    [divisions]
  )

  const handleBack = useCallback(() => {
    history.goBack()
  }, [history])

  const handleCancel = useCallback(() => {
    history.push(`/schedule/${competitionId}`)
  }, [history, competitionId])

  // ─── Publish Handlers ────────────────────────────────────
  const handlePublishClick = useCallback(() => {
    setIsPublishModalOpen(true)
  }, [])

  const handlePublishConfirm = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    try {
      await req(`/scheduler/requests/${schedulerRequest.id}/apply`, {
        method: 'POST',
      })
      setIsPublishModalOpen(false)
      history.push(`/schedule/${competitionId}`)
    } catch {
      // Error is handled by the hook
    }
  }, [schedulerRequest?.id, history, competitionId])

  // Generate modal state
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [previewPayload, setPreviewPayload] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  // Track the selected division IDs for the loading state
  const [generatingDivisionIds, setGeneratingDivisionIds] = useState<string[]>(
    []
  )
  // Track user-defined division priority order for progress display
  const [divisionPriorityOrder, setDivisionPriorityOrder] = useState<string[]>(
    []
  )
  // Success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false)
  const wasGeneratingRef = useRef(false)
  // Track total generation elapsed time (seconds)
  const generationStartRef = useRef<number | null>(null)
  const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState<
    number | null
  >(null)
  // Tracks whether the user explicitly triggered generation in this session
  const userTriggeredGenerateRef = useRef(false)
  // Generate error state
  const [generateError, setGenerateError] = useState<string | null>(null)

  const handleGenerate = () => {
    setIsGenerateModalOpen(true)
  }

  const handleGenerateModalClose = () => {
    setIsGenerateModalOpen(false)
    setPreviewPayload(null)
    setIsLoadingPreview(false)
  }

  // Cancel modal & retrying state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [reviewingPartialGames, setReviewingPartialGames] = useState(false)

  // Publish state
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)

  // Determine the review tab's status based on scheduler request
  const requestStatus = schedulerRequest?.status
  const isGenerating =
    userTriggeredGenerateRef.current &&
    (isPolling || requestStatus === 'pending' || requestStatus === 'processing')
  const isGenerated =
    requestStatus === 'generated' || requestStatus === 'applied'
  const isPartiallyGenerated = requestStatus === 'partially_generated'
  const isFailed =
    userTriggeredGenerateRef.current && requestStatus === 'failed'
  const isCancelled = requestStatus === 'cancelled'

  // Track when we enter the generating state
  useEffect(() => {
    if (isGenerating) {
      wasGeneratingRef.current = true
      if (!generationStartRef.current) {
        generationStartRef.current = Date.now()
        setGenerationElapsedSeconds(null)
      }
    }
  }, [isGenerating])

  // Show success animation on generating → generated transition
  useEffect(() => {
    if (isGenerated && wasGeneratingRef.current) {
      wasGeneratingRef.current = false
      // Capture elapsed time
      if (generationStartRef.current) {
        setGenerationElapsedSeconds(
          Math.round((Date.now() - generationStartRef.current) / 1000)
        )
        generationStartRef.current = null
      }
      setShowSuccessAnimation(true)
      const timer = setTimeout(() => {
        setShowSuccessAnimation(false)
      }, SUCCESS_ANIMATION_DURATION_MS)
      return () => clearTimeout(timer)
    }
  }, [isGenerated])

  // Reset partial review mode when status changes away from partially_generated
  useEffect(() => {
    if (!isPartiallyGenerated) {
      setReviewingPartialGames(false)
    }
  }, [isPartiallyGenerated])

  // Auto-navigate to Review Schedule tab when resuming a request that has games
  useEffect(() => {
    if (!isResuming || !schedulerRequest) {
      return
    }
    const status = schedulerRequest.status
    if (
      status === 'generated' ||
      status === 'applied' ||
      status === 'partially_generated'
    ) {
      setActiveNavTab('review-schedule')
    }
  }, [isResuming, schedulerRequest])

  const handleGenerateConfirm = useCallback(
    async (
      selectedDivisionIds: string[],
      selectedLocationIds: string[],
      divisionPriority: string[]
    ) => {
      if (!competition) {
        return
      }

      userTriggeredGenerateRef.current = true
      setGenerateError(null)
      clearGameEditError()
      setGeneratingDivisionIds(selectedDivisionIds)
      setDivisionPriorityOrder(divisionPriority)

      // Pre-validate configuration before calling the API
      const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length
      if (activeTimeSlotCount === 0) {
        setGenerateError(
          'No time slots configured. Please add time slots to your venues before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }
      if (venues.length === 0) {
        setGenerateError(
          'No venues configured. Please add at least one venue with facilities before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }
      if (!competition.starts_at || !competition.ends_at) {
        setGenerateError(
          'Date range not configured. Please set a start and end date before generating a schedule.'
        )
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
        return
      }

      try {
        // Refetch constraints to guarantee fresh server state.
        // This eliminates race conditions where the user edits a constraint
        // and clicks Generate before React state propagates.
        let freshConstraints = constraints
        try {
          freshConstraints = await refetchConstraints()
        } catch {
          console.warn(
            'Pre-generate constraint refetch failed, using local state'
          )
        }

        const payload = buildGeneratePayload(
          competition,
          divisions,
          venues,
          timeSlots,
          freshConstraints,
          selectedDivisionIds,
          selectedLocationIds,
          requestId,
          divisionPriority
        )

        if (payload.dry_run) {
          // Dry run: show payload preview in modal
          setIsLoadingPreview(true)
          try {
            const { data } = await req('/scheduler/generate', {
              method: 'POST',
              body: JSON.stringify(payload),
            })
            setPreviewPayload(data?.data ?? data)
          } finally {
            setIsLoadingPreview(false)
          }
          return
        }

        // Real generation: call API, close modal, switch to Review tab, start polling
        const { data } = await req('/scheduler/generate', {
          method: 'POST',
          body: JSON.stringify(payload),
        })

        const responseData = data?.data ?? data
        const newRequestUid = responseData?.id

        // Close modal and switch to review tab
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')

        if (newRequestUid) {
          // Update URL with request ID
          history.replace(
            `/scheduler-wizard/${competitionId}/configure/${newRequestUid}`
          )
          // Start polling for status with division count for dynamic timeout
          startPolling(newRequestUid, selectedDivisionIds.length)
        }
      } catch (err) {
        setGenerateError(
          err instanceof Error
            ? err.message
            : 'Failed to generate schedule. Please check your configuration and try again.'
        )
        // Close modal and go to review tab to show error
        setIsGenerateModalOpen(false)
        setActiveNavTab('review-schedule')
      }
    },
    [
      competition,
      divisions,
      venues,
      timeSlots,
      constraints,
      refetchConstraints,
      history,
      competitionId,
      startPolling,
      requestId,
      clearGameEditError,
    ]
  )

  // Retry handler (full retry for failed/cancelled)
  const handleRetry = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    userTriggeredGenerateRef.current = true
    setGenerateError(null)
    setIsRetrying(true)

    try {
      const success = await retryRequest(schedulerRequest.id)
      if (success) {
        const divCount =
          schedulerRequest.division_jobs?.length ?? generatingDivisionIds.length
        startPolling(schedulerRequest.id, divCount)
      }
    } catch (err) {
      setGenerateError(
        err instanceof Error
          ? err.message
          : 'Failed to retry schedule generation'
      )
    } finally {
      setIsRetrying(false)
    }
  }, [
    schedulerRequest?.id,
    schedulerRequest?.division_jobs?.length,
    retryRequest,
    startPolling,
    generatingDivisionIds.length,
  ])

  // Retry only failed divisions (from partially_generated state)
  const handleRetryFailed = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    userTriggeredGenerateRef.current = true
    setGenerateError(null)
    setIsRetrying(true)

    try {
      const success = await retryFailedDivisions(schedulerRequest.id)
      if (success) {
        const divCount = schedulerRequest.division_jobs?.length ?? 1
        startPolling(schedulerRequest.id, divCount)
      }
    } catch (err) {
      setGenerateError(
        err instanceof Error ? err.message : 'Failed to retry failed divisions'
      )
    } finally {
      setIsRetrying(false)
    }
  }, [
    schedulerRequest?.id,
    schedulerRequest?.division_jobs?.length,
    retryFailedDivisions,
    startPolling,
  ])

  // Reset all divisions (from partially_generated state)
  const handleResetAllDivisions = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    const allDivisionIds = (schedulerRequest.division_jobs ?? []).map(
      (j) => j.division_id
    )
    if (allDivisionIds.length === 0) {
      return
    }

    setIsRetrying(true)
    try {
      const success = await resetDivisions(schedulerRequest.id, allDivisionIds)
      if (success) {
        await fetchRequest(schedulerRequest.id)
      }
    } catch (err) {
      setGenerateError(
        err instanceof Error ? err.message : 'Failed to reset divisions'
      )
    } finally {
      setIsRetrying(false)
    }
  }, [
    schedulerRequest?.id,
    schedulerRequest?.division_jobs,
    resetDivisions,
    fetchRequest,
  ])

  // Approve completed divisions (navigate to review table filtered to completed)
  const handleApproveCompletedDivisions = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    try {
      // Re-fetch to get games, then show the review content
      await fetchRequest(schedulerRequest.id)
      setReviewingPartialGames(true)
      setActiveNavTab('review-schedule')
    } catch (err) {
      setGenerateError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch completed divisions'
      )
    }
  }, [schedulerRequest?.id, fetchRequest])

  // Cancel generation handler
  const handleCancelGeneration = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    setIsCancelling(true)
    try {
      await cancelRequest(schedulerRequest.id)
    } finally {
      setIsCancelling(false)
      setIsCancelModalOpen(false)
    }
  }, [schedulerRequest?.id, cancelRequest])

  // Approve all handler
  const handleApproveAll = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    try {
      const success = await approveAllGames(schedulerRequest.id)
      if (success) {
        updateAllGamesApproved()
      }
    } catch (err) {
      setGenerateError(
        err instanceof Error ? err.message : 'Failed to approve all games'
      )
    }
  }, [schedulerRequest?.id, approveAllGames, updateAllGamesApproved])

  // Reset divisions handler – removes generated games and reverts to pending
  const handleResetDivisions = useCallback(
    async (divisionUids: string[]): Promise<boolean> => {
      if (!schedulerRequest?.id) {
        return false
      }
      const success = await resetDivisions(schedulerRequest.id, divisionUids)
      if (success) {
        await fetchRequest(schedulerRequest.id)
      }
      return success
    },
    [schedulerRequest?.id, resetDivisions, fetchRequest]
  )

  // Approve by divisions handler for ScheduleReviewTable
  const handleApproveByDivisions = useCallback(
    async (divisionUids: string[]) => {
      if (!schedulerRequest?.id) {
        return
      }
      try {
        const success = await approveByDivisions(
          schedulerRequest.id,
          divisionUids
        )
        if (success) {
          updateDivisionApprovalStatus(divisionUids, 'approved')
        }
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to approve divisions'
        )
      }
    },
    [schedulerRequest?.id, approveByDivisions, updateDivisionApprovalStatus]
  )

  // Bulk approve games handler for GeneratedGamesSection
  const handleBulkApproveGames = useCallback(
    async (gameIds: string[]) => {
      if (!schedulerRequest?.id) {
        return
      }
      try {
        const success = await bulkApproveGames(schedulerRequest.id, gameIds)
        if (success) {
          updateBulkGameStatus(gameIds, 'approved')
        }
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to approve games'
        )
      }
    },
    [schedulerRequest?.id, bulkApproveGames, updateBulkGameStatus]
  )

  // Bulk revert games handler for GeneratedGamesSection
  const handleBulkRevertGames = useCallback(
    async (gameIds: string[]) => {
      if (!schedulerRequest?.id) {
        return
      }
      try {
        const success = await bulkRevertGames(schedulerRequest.id, gameIds)
        if (success) {
          updateBulkGameStatus(gameIds, 'pending')
        }
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to revert games'
        )
      }
    },
    [schedulerRequest?.id, bulkRevertGames, updateBulkGameStatus]
  )

  // Revert by divisions handler for ScheduleReviewTable
  const handleRevertByDivisions = useCallback(
    async (divisionUids: string[]) => {
      if (!schedulerRequest?.id) {
        return
      }
      try {
        const success = await revertByDivisions(
          schedulerRequest.id,
          divisionUids
        )
        if (success) {
          updateDivisionApprovalStatus(divisionUids, 'pending')
        }
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to revert divisions'
        )
      }
    },
    [schedulerRequest?.id, revertByDivisions, updateDivisionApprovalStatus]
  )

  // Individual game approve handler
  const handleApproveGame = useCallback(
    async (gameId: string) => {
      if (!schedulerRequest?.id) {
        return false
      }
      try {
        const success = await approveGame(schedulerRequest.id, gameId)
        if (success) {
          updateGameStatus(gameId, 'approved')
        }
        return success
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to approve game'
        )
        return false
      }
    },
    [schedulerRequest?.id, approveGame, updateGameStatus]
  )

  // Individual game unapprove handler
  const handleUnapproveGame = useCallback(
    async (gameId: string) => {
      if (!schedulerRequest?.id) {
        return false
      }
      try {
        const success = await unapproveGame(schedulerRequest.id, gameId)
        if (success) {
          updateGameStatus(gameId, 'pending')
        }
        return success
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to unapprove game'
        )
        return false
      }
    },
    [schedulerRequest?.id, unapproveGame, updateGameStatus]
  )

  // Save game edit handler — routes through preview modal
  const handleSaveGame = useCallback(
    async (gameId: string, data: GameEditData) => {
      if (!schedulerRequest?.id) {
        return false
      }
      try {
        await requestMutationPreview(schedulerRequest.id, gameId, data)
        return true
      } catch (err) {
        setGenerateError(
          err instanceof Error ? err.message : 'Failed to preview game'
        )
        return false
      }
    },
    [schedulerRequest?.id, requestMutationPreview]
  )

  // Map hook divisions to GenerateScheduleModal Division[] format
  const modalDivisions: Division[] = useMemo(() => {
    const games = schedulerRequest?.games ?? []
    const approvedUids = schedulerRequest?.approved_division_uids ?? []

    return divisions.map((d) => {
      // Compute real status per division from games
      const divGames = games.filter((g) => g.division_uid === d.id)
      let scheduleStatus: 'pending' | 'generated' | 'approved' = 'pending'

      if (divGames.length > 0) {
        const allApproved = divGames.every(
          (g) => g.approval_status === 'approved'
        )
        scheduleStatus = allApproved ? 'approved' : 'generated'
      }

      const isApproved = approvedUids.includes(d.id)

      return {
        id: d.id,
        name: d.name,
        locations:
          venues.length > 0
            ? venues.map((v) => ({
                id: v.id,
                name: v.name,
                surfaceCount: v.facilities.length,
              }))
            : 'all',
        status:
          scheduleStatus === 'pending'
            ? 'Schedule Pending'
            : scheduleStatus === 'generated'
            ? 'Generated'
            : 'Approved',
        scheduleStatus,
        isDisabled: isApproved,
        isApproved,
      }
    })
  }, [
    divisions,
    venues,
    schedulerRequest?.games,
    schedulerRequest?.approved_division_uids,
  ])

  // Transform real data for review components
  // Whether review data should be computed (fully generated OR reviewing partial results)
  const shouldShowReviewData =
    isGenerated || (isPartiallyGenerated && reviewingPartialGames)

  const summaryStats = useMemo(() => {
    if (!schedulerRequest || !shouldShowReviewData) {
      return null
    }

    const violations = schedulerRequest.violations ?? []
    const errors = violations.filter((v) => v.severity === 'error').length
    const warnings = violations.filter((v) => v.severity === 'warning').length

    return {
      totalGames: schedulerRequest.total_games_count,
      totalConflicts: errors,
      totalExceptions: warnings,
      scheduleScore: getScoreLabel(schedulerRequest.satisfaction_score),
    }
  }, [schedulerRequest, shouldShowReviewData])

  const reviewDivisions: DivisionRow[] = useMemo(() => {
    if (!schedulerRequest?.games || !shouldShowReviewData) {
      return []
    }
    return transformGamesToDivisionRows(
      schedulerRequest.games,
      divisions,
      schedulerRequest.satisfaction_score
    )
  }, [
    schedulerRequest?.games,
    shouldShowReviewData,
    divisions,
    schedulerRequest?.satisfaction_score,
  ])

  const displayGames: GeneratedGame[] = useMemo(() => {
    if (!schedulerRequest?.games || !shouldShowReviewData) {
      return []
    }
    return transformGamesToDisplay(schedulerRequest.games, divisions)
  }, [schedulerRequest?.games, shouldShowReviewData, divisions])

  // ─── Filter state & logic ──────────────────────────────────
  const [gameFilters, setGameFilters] = useState<ScheduleFilterValues>(
    DEFAULT_FILTER_VALUES
  )

  // Extract unique filter options from games
  const filterOptions = useMemo(() => {
    const divisionSet = new Map<string, string>()
    const teamSet = new Map<string, string>()
    const locationSet = new Map<string, string>()
    const surfaceSet = new Map<string, string>()

    for (const game of displayGames) {
      if (game.divisionId) {
        divisionSet.set(game.divisionId, game.division)
      }
      if (game.homeTeam && game.homeTeam !== 'TBD') {
        teamSet.set(game.homeTeam, game.homeTeam)
      }
      if (game.awayTeam && game.awayTeam !== 'TBD') {
        teamSet.set(game.awayTeam, game.awayTeam)
      }
      if (game.location && game.location !== 'Unknown Venue') {
        locationSet.set(game.location, game.location)
      }
      if (game.surface && game.surface !== 'Unknown Surface') {
        surfaceSet.set(game.surface, game.surface)
      }
    }

    const toOptions = (map: Map<string, string>) =>
      Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label))

    const statusOptions = [
      { value: 'approved', label: 'Approved' },
      { value: 'pending', label: 'Pending' },
      { value: 'rejected', label: 'Rejected' },
    ]

    return {
      divisions: toOptions(divisionSet),
      teams: toOptions(teamSet),
      locations: toOptions(locationSet),
      surfaces: toOptions(surfaceSet),
      statuses: statusOptions,
    }
  }, [displayGames])

  // Apply filters to games
  const filteredGames: GeneratedGame[] = useMemo(() => {
    return displayGames.filter((game) => {
      if (
        gameFilters.division.length > 0 &&
        !gameFilters.division.includes(game.divisionId)
      ) {
        return false
      }
      if (
        gameFilters.team.length > 0 &&
        !gameFilters.team.includes(game.homeTeam) &&
        !gameFilters.team.includes(game.awayTeam)
      ) {
        return false
      }
      if (
        gameFilters.location.length > 0 &&
        !gameFilters.location.includes(game.location)
      ) {
        return false
      }
      if (
        gameFilters.surface.length > 0 &&
        !gameFilters.surface.includes(game.surface)
      ) {
        return false
      }
      if (gameFilters.status.length > 0) {
        const gameStatus = game.approvalStatus ?? 'pending'
        if (!gameFilters.status.includes(gameStatus)) {
          return false
        }
      }
      return true
    })
  }, [displayGames, gameFilters])

  // Export all generated games to CSV
  const handleExportGames = useCallback(() => {
    if (displayGames.length === 0) {
      return
    }
    const csv = generateGamesCsv({ games: displayGames, venues })
    const name = (competition?.name || 'Schedule')
      .replace(/[^a-zA-Z0-9_\- ]/g, '')
      .replace(/\s+/g, '_')
    downloadCsv(csv, `${name}_Games_${dayjs().format('YYYYMMDD')}.csv`)
  }, [displayGames, venues, competition])

  // ─── Checkpoint handlers ────────────────────────────────
  const handleSaveCheckpoint = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    const doSave = async () => {
      const success = await saveCheckpoint(schedulerRequest.id)
      if (success) {
        await fetchRequest(schedulerRequest.id)
        message.success('Saved')
      } else {
        message.error('Failed to save')
      }
    }

    if (schedulerRequest?.has_checkpoint) {
      modal.confirm({
        title: 'Overwrite Save',
        content: 'This will overwrite the existing save point. Are you sure?',
        okText: 'Overwrite',
        onOk: doSave,
        centered: true,
      })
    } else {
      await doSave()
    }
  }, [
    schedulerRequest?.id,
    schedulerRequest?.has_checkpoint,
    saveCheckpoint,
    fetchRequest,
    message,
    modal,
  ])

  const handleRestoreCheckpoint = useCallback(async () => {
    if (!schedulerRequest?.id) {
      return
    }
    modal.confirm({
      title: 'Undo Changes',
      content:
        'All changes since the last save will be lost. This cannot be undone.',
      okText: 'Undo',
      okButtonProps: { danger: true },
      onOk: async () => {
        const { success } = await restoreCheckpoint(schedulerRequest.id)
        if (success) {
          await fetchRequest(schedulerRequest.id)
          message.success('Changes undone')
        } else {
          message.error('Failed to undo changes')
        }
      },
      centered: true,
    })
  }, [schedulerRequest?.id, restoreCheckpoint, fetchRequest, message, modal])

  // ─── Dynamic navigation tab statuses ──────────────────────
  const navigationTabs: NavigationTab[] = useMemo(() => {
    // --- Time Slots tab ---
    const hasDateRange = !!(competition?.starts_at && competition?.ends_at)
    const venueCount = venues.length
    const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length

    let timeSlotsStatus: NavigationTab['status'] = 'incomplete'
    let timeSlotsSubtitle = 'Not configured'

    if (hasDateRange && venueCount > 0 && activeTimeSlotCount > 0) {
      timeSlotsStatus = 'ready'
      timeSlotsSubtitle = `${venueCount} venue${
        venueCount !== 1 ? 's' : ''
      }, ${activeTimeSlotCount} time slot${
        activeTimeSlotCount !== 1 ? 's' : ''
      }`
    } else if (venueCount > 0 || activeTimeSlotCount > 0) {
      timeSlotsStatus = 'pending'
      if (venueCount === 0) {
        timeSlotsSubtitle = 'Needs venues'
      } else if (activeTimeSlotCount === 0) {
        timeSlotsSubtitle = 'Needs time slots'
      } else {
        timeSlotsSubtitle = 'Needs date range'
      }
    }

    // --- Divisions & Teams tab ---
    const totalTeams = divisions.reduce((sum, d) => sum + d.teamsCount, 0)
    const divisionsNeedingTeams = divisions.filter(
      (d) => d.teamsCount < MIN_TEAMS_PER_DIVISION
    ).length

    let divisionsStatus: NavigationTab['status'] = 'incomplete'
    let divisionsSubtitle = 'No divisions added'

    if (divisions.length > 0 && divisionsNeedingTeams === 0) {
      divisionsStatus = 'ready'
      divisionsSubtitle = `${divisions.length} division${
        divisions.length !== 1 ? 's' : ''
      }, ${totalTeams} team${totalTeams !== 1 ? 's' : ''}`
    } else if (divisions.length > 0) {
      divisionsStatus = 'pending'
      divisionsSubtitle = `${divisionsNeedingTeams} division${
        divisionsNeedingTeams !== 1 ? 's' : ''
      } need${divisionsNeedingTeams === 1 ? 's' : ''} teams`
    }

    // --- Constraints tab ---
    const tableConstraints = constraints.filter(
      (c) => !BLACKOUT_BYE_CONSTRAINT_CODES.includes(c.type.code)
    )
    const enabledCount = tableConstraints.filter((c) => c.is_enabled).length

    // Check for enabled non-venue constraints missing division/team scope
    const VENUE_SCOPE_CODES = ['venue_capacity', 'venue_blackout']
    const unconfiguredNonVenueCount = tableConstraints.filter(
      (c) =>
        c.is_enabled &&
        !VENUE_SCOPE_CODES.includes(c.type.code) &&
        (c.division_uids === null || c.team_uids === null)
    ).length

    // Check for enabled venue_capacity without facilities
    const unconfiguredVenueCount = tableConstraints.filter(
      (c) =>
        c.is_enabled &&
        c.type.code === 'venue_capacity' &&
        (!(c.parameters as { facility_uids?: string[] })?.facility_uids ||
          (c.parameters as { facility_uids?: string[] })?.facility_uids
            ?.length === 0)
    ).length

    const unconfiguredCount = unconfiguredNonVenueCount + unconfiguredVenueCount

    let constraintsStatus: NavigationTab['status'] = 'incomplete'
    let constraintsSubtitle = 'No constraints added'

    if (tableConstraints.length > 0 && enabledCount > 0) {
      if (unconfiguredCount > 0) {
        constraintsStatus = 'pending'
        constraintsSubtitle = `${unconfiguredCount} constraint${
          unconfiguredCount !== 1 ? 's' : ''
        } need${unconfiguredCount === 1 ? 's' : ''} configuration`
      } else {
        constraintsStatus = 'ready'
        constraintsSubtitle = `${enabledCount} active constraint${
          enabledCount !== 1 ? 's' : ''
        }`
      }
    } else if (tableConstraints.length > 0) {
      constraintsStatus = 'pending'
      constraintsSubtitle = 'No active constraints'
    }

    // --- Review Schedule tab ---
    let reviewStatus: NavigationTab['status'] = 'incomplete'
    let reviewSubtitle = 'Generate schedule first'

    if (generateError) {
      reviewStatus = 'incomplete'
      reviewSubtitle = 'Failed'
    } else if (isGenerating) {
      reviewStatus = 'pending'
      reviewSubtitle = 'Generating...'
    } else if (isPartiallyGenerated) {
      const completedCount = (schedulerRequest?.division_jobs ?? []).filter(
        (j) => j.status === 'completed'
      ).length
      const totalJobs = schedulerRequest?.division_jobs?.length ?? 0
      reviewStatus = 'pending'
      reviewSubtitle = `${completedCount}/${totalJobs} divisions completed`
    } else if (isFailed || isCancelled) {
      reviewStatus = 'incomplete'
      reviewSubtitle = isCancelled ? 'Cancelled' : 'Failed'
    } else if (isGenerated && summaryStats) {
      if (summaryStats.totalConflicts === 0) {
        reviewStatus = 'ready'
        reviewSubtitle = `${summaryStats.totalGames} game${
          summaryStats.totalGames !== 1 ? 's' : ''
        }, no conflicts`
      } else {
        reviewStatus = 'pending'
        reviewSubtitle = `${summaryStats.totalConflicts} conflict${
          summaryStats.totalConflicts !== 1 ? 's' : ''
        } to review`
      }
    }

    return [
      {
        id: 'time-slots',
        title: 'Time Slots',
        subtitle: timeSlotsSubtitle,
        status: timeSlotsStatus,
      },
      {
        id: 'divisions-teams',
        title: 'Divisions & Teams',
        subtitle: divisionsSubtitle,
        status: divisionsStatus,
      },
      {
        id: 'constraints',
        title: 'Constraints',
        subtitle: constraintsSubtitle,
        status: constraintsStatus,
      },
      {
        id: 'review-schedule',
        title: 'Review Schedule',
        subtitle: reviewSubtitle,
        status: reviewStatus,
        disabled:
          !generateError &&
          !isGenerating &&
          !isGenerated &&
          !isPartiallyGenerated &&
          !isFailed &&
          !isCancelled,
      },
    ]
  }, [
    competition?.starts_at,
    competition?.ends_at,
    venues.length,
    timeSlots,
    divisions,
    constraints,
    generateError,
    isGenerating,
    isGenerated,
    isPartiallyGenerated,
    isFailed,
    isCancelled,
    summaryStats,
    schedulerRequest?.division_jobs,
  ])

  // ─── Generate button validation ──────────────────────
  const { canGenerate, generateDisabledReason } = useMemo(() => {
    if (isPolling) {
      return { canGenerate: false, generateDisabledReason: '' }
    }

    const hasDateRange = !!(competition?.starts_at && competition?.ends_at)
    const activeTimeSlotCount = timeSlots.filter((ts) => ts.is_active).length
    const venueCount = venues.length
    const hasDivisions = divisions.length > 0
    const allDivisionsHaveTeams = divisions.every(
      (d) => d.teamsCount >= MIN_TEAMS_PER_DIVISION
    )

    // Check for enabled non-venue constraints missing division/team scope
    const VENUE_SCOPE_CODES = ['venue_capacity', 'venue_blackout']
    const unconfiguredConstraints = constraints.filter(
      (c) =>
        c.is_enabled &&
        !BLACKOUT_BYE_CONSTRAINT_CODES.includes(c.type.code) &&
        !VENUE_SCOPE_CODES.includes(c.type.code) &&
        (c.division_uids === null || c.team_uids === null)
    )

    // Check for enabled venue_capacity without facilities
    const unconfiguredVenueConstraints = constraints.filter(
      (c) =>
        c.is_enabled &&
        c.type.code === 'venue_capacity' &&
        (!(c.parameters as { facility_uids?: string[] })?.facility_uids ||
          (c.parameters as { facility_uids?: string[] })?.facility_uids
            ?.length === 0)
    )

    const issues: string[] = []
    if (!hasDateRange) {
      issues.push('a date range')
    }
    if (venueCount === 0) {
      issues.push('at least one venue')
    }
    if (activeTimeSlotCount === 0) {
      issues.push('at least one time slot')
    }
    if (!hasDivisions) {
      issues.push('at least one division')
    } else if (!allDivisionsHaveTeams) {
      issues.push('at least 2 teams per division')
    }
    if (unconfiguredConstraints.length > 0) {
      issues.push('divisions and teams for all enabled constraints')
    }
    if (unconfiguredVenueConstraints.length > 0) {
      issues.push('facilities for Venue Capacity constraint')
    }

    if (issues.length > 0) {
      return {
        canGenerate: false,
        generateDisabledReason: `Configure ${issues.join(
          ', '
        )} before generating`,
      }
    }
    return { canGenerate: true, generateDisabledReason: '' }
  }, [
    isPolling,
    competition?.starts_at,
    competition?.ends_at,
    timeSlots,
    venues.length,
    divisions,
    constraints,
  ])

  // Division & Teams handlers
  const handleAddDivision = useCallback(
    async (divisionName: string) => {
      if (!competition?.organization_id) {
        return
      }
      await addDivision(
        competition.organization_id,
        competitionId,
        divisionName,
        competition.starts_at || new Date().toISOString(),
        competition.ends_at || new Date().toISOString()
      )
    },
    [addDivision, competition, competitionId]
  )

  const handleAddTeam = useCallback(
    async (divisionId: string, teamName: string) => {
      await addTeam(divisionId, teamName)
    },
    [addTeam]
  )

  const handleDeleteTeam = useCallback(
    (divisionId: string, teamId: string, teamName: string) => {
      Modal.confirm({
        title: 'Remove Team',
        content: `Are you sure you want to remove "${teamName}"?`,
        okText: 'Remove',
        okButtonProps: { danger: true },
        onOk: () => deleteTeam(divisionId, teamId),
        centered: true,
      })
    },
    [deleteTeam]
  )

  const handleDeleteDivision = useCallback(
    (divisionId: string, divisionName: string) => {
      Modal.confirm({
        title: 'Delete Division',
        content: `Are you sure you want to delete "${divisionName}" and all its teams?`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () => deleteDivision(divisionId, true),
        centered: true,
      })
    },
    [deleteDivision]
  )

  // Handler for when a new request is created (lazy creation)
  const handleRequestCreated = useCallback(
    (newRequestId: string) => {
      // Update the URL to include the requestId without full page reload
      history.replace(
        `/scheduler-wizard/${competitionId}/configure/${newRequestId}`
      )
    },
    [history, competitionId]
  )

  // Success animation
  const renderSuccessAnimation = () => (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 72px 0;
        animation: ${successFadeIn} 0.35s ease-out;
      `}
    >
      {/* Glowing circle with checkmark */}
      <div
        css={css`
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 20px;
        `}
      >
        {/* Outer glow ring */}
        <div
          css={css`
            position: absolute;
            inset: -6px;
            border-radius: 50%;
            background: radial-gradient(
              circle,
              rgba(16, 173, 50, 0.2) 0%,
              transparent 70%
            );
            animation: ${successFadeIn} 0.6s ease-out;
          `}
        />
        {/* Circle background */}
        <div
          css={css`
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: linear-gradient(
              135deg,
              ${colors.SUCCESS_300} 0%,
              ${colors.SUCCESS_200} 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${checkmarkPop} 0.45s ease-out;
            box-shadow: 0 4px 24px rgba(16, 173, 50, 0.3);
          `}
        >
          <svg width='36' height='36' viewBox='0 0 24 24' fill='none'>
            <path
              d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
              fill={colors.WHITE}
              strokeWidth='0.5'
              stroke={colors.WHITE}
            />
          </svg>
        </div>
      </div>
      <p
        css={css`
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 600;
          font-size: 26px;
          color: ${colors.WHITE};
          margin: 0 0 6px;
        `}
      >
        Schedule Generated
      </p>
      <p
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 14px;
          color: ${colors.NEUTRAL_50};
          margin: 0;
          animation: ${fadeInUp} 0.4s ease-out 0.2s both;
        `}
      >
        Preparing your results...
      </p>
    </div>
  )

  // Error state
  const renderErrorState = () => {
    const wasCancelled = isCancelled
    const iconColor = wasCancelled ? colors.WARNING_300 : colors.ERROR_200
    const title = wasCancelled
      ? 'Schedule Generation Cancelled'
      : 'Schedule Generation Failed'
    const defaultMessage = wasCancelled
      ? 'The schedule generation was cancelled. You can retry to start again.'
      : 'An unexpected error occurred. Please try again.'

    const errorMessage =
      generateError ||
      (schedulerRequest?.error
        ? typeof schedulerRequest.error === 'object' &&
          schedulerRequest.error.message
          ? schedulerRequest.error.message
          : String(schedulerRequest.error)
        : defaultMessage)

    // Determine if this is a configuration issue that can be fixed via a specific tab
    const isConfigError = !!generateError && !schedulerRequest?.error
    const needsTimeSlots =
      isConfigError &&
      (errorMessage.toLowerCase().includes('time slot') ||
        errorMessage.toLowerCase().includes('venue'))

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 60px 0;
        `}
      >
        <div
          css={css`
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background-color: ${iconColor};
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
            {wasCancelled ? (
              <path
                d='M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z'
                fill={colors.WHITE}
              />
            ) : (
              <path
                d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'
                fill={colors.WHITE}
              />
            )}
          </svg>
        </div>
        <p
          css={css`
            font-family: 'Barlow Condensed', sans-serif;
            font-weight: 600;
            font-size: 24px;
            color: ${iconColor};
            margin: 0;
          `}
        >
          {title}
        </p>
        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 400;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
            margin: 0;
            max-width: 400px;
            text-align: center;
          `}
        >
          {errorMessage}
        </p>
        {isConfigError && (
          <button
            type='button'
            onClick={() => {
              setGenerateError(null)
              setActiveNavTab(needsTimeSlots ? 'time-slots' : 'divisions-teams')
            }}
            css={css`
              padding: 10px 24px;
              background: ${colors.PRIMARY_300};
              border: none;
              border-radius: 8px;
              color: ${colors.SECONDARY_300};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: background 0.2s ease;
              &:hover {
                background: ${colors.PRIMARY_400};
              }
            `}
          >
            {needsTimeSlots ? 'Go to Time Slots' : 'Go to Divisions & Teams'}
          </button>
        )}
        {!isConfigError &&
          (schedulerRequest?.can_retry ||
            schedulerRequest?.capabilities?.can_retry) && (
            <button
              type='button'
              onClick={handleRetry}
              disabled={isRetrying}
              css={css`
                padding: 10px 24px;
                background: ${colors.PRIMARY_300};
                border: none;
                border-radius: 8px;
                color: ${colors.SECONDARY_300};
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.2s ease;
                &:hover {
                  background: ${colors.PRIMARY_400};
                }
                &:disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                }
              `}
            >
              {isRetrying ? 'Retrying...' : 'Retry Generation'}
            </button>
          )}
      </div>
    )
  }

  // Determine what to render in the review tab
  const renderReviewContent = () => {
    // Show success animation after generating → generated transition.
    // Check the ref synchronously so the very first render after 'generated'
    // shows the success screen (useEffect hasn't fired yet on that frame).
    if (showSuccessAnimation || (isGenerated && wasGeneratingRef.current)) {
      return renderSuccessAnimation()
    }

    // Show error state (failed or cancelled)
    if (isFailed || isCancelled || generateError) {
      return renderErrorState()
    }

    // Show loading/curating state
    if (isGenerating) {
      return (
        <CuratingState
          isGenerating={isGenerating}
          generatingDivisionIds={generatingDivisionIds}
          divisions={divisions}
          schedulerRequest={schedulerRequest}
          divisionPriorityOrder={divisionPriorityOrder}
          generationStartTime={generationStartRef.current}
          isTimedOut={isTimedOut}
          timeoutReason={timeoutReason}
          keepWaiting={keepWaiting}
          onCancelClick={() => setIsCancelModalOpen(true)}
        />
      )
    }

    // Show partially generated state (unless user clicked "Review Completed Divisions")
    if (isPartiallyGenerated && schedulerRequest && !reviewingPartialGames) {
      return (
        <PartiallyGeneratedState
          divisionJobs={sortJobsByPriority(
            schedulerRequest.division_jobs ?? [],
            divisionPriorityOrder.length > 0
              ? divisionPriorityOrder
              : schedulerRequest.division_dispatch_order
          )}
          capabilities={schedulerRequest.capabilities}
          satisfactionScore={schedulerRequest.satisfaction_score}
          onRetryFailed={handleRetryFailed}
          onResetAll={handleResetAllDivisions}
          onApproveCompleted={handleApproveCompletedDivisions}
          retrying={isRetrying}
        />
      )
    }

    // Show real data when generated (or reviewing partial games)
    if (
      (isGenerated || (isPartiallyGenerated && reviewingPartialGames)) &&
      schedulerRequest
    ) {
      return (
        <>
          {reviewingPartialGames && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 10px 16px;
                margin-bottom: 16px;
                background: ${colors.WARNING_300}10;
                border: 1px solid ${colors.WARNING_300}30;
                border-radius: 8px;
              `}
            >
              <button
                type='button'
                onClick={() => setReviewingPartialGames(false)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  background: transparent;
                  border: none;
                  padding: 0;
                  cursor: pointer;
                  color: ${colors.PRIMARY_300};
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  font-weight: 600;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z'
                    fill={colors.PRIMARY_300}
                  />
                </svg>
                Back to Summary
              </button>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  color: ${colors.NEUTRAL_70};
                `}
              >
                Showing completed divisions only. Failed divisions are excluded.
              </span>
            </div>
          )}
          <ScheduleSummary
            stats={summaryStats}
            startsAt={competition?.starts_at}
            endsAt={competition?.ends_at}
            generationElapsedSeconds={generationElapsedSeconds}
          >
            {/* <ScheduleChartsSection games={filteredGames} /> */}
          </ScheduleSummary>
          <hr
            css={css`
              border: none;
              border-top: 1px solid ${colors.SECONDARY_100};
              margin: 16px 0;
              width: 100%;
            `}
          />
          <ScheduleReviewTable
            divisions={reviewDivisions}
            loading={gameApproving}
            onApproveAll={handleApproveAll}
            onApproveByDivisions={handleApproveByDivisions}
            onRevertByDivisions={handleRevertByDivisions}
          />
          <hr
            css={css`
              border: none;
              border-top: 1px solid ${colors.SECONDARY_100};
              margin: 16px 0;
              width: 100%;
            `}
          />
          <GeneratedGamesSection
            games={filteredGames}
            totalGames={schedulerRequest.total_games_count}
            onExport={handleExportGames}
            onApproveAll={handleApproveAll}
            onApproveGame={handleApproveGame}
            onUnapproveGame={handleUnapproveGame}
            onBulkApprove={handleBulkApproveGames}
            onBulkRevert={handleBulkRevertGames}
            onSaveGame={handleSaveGame}
            editingGameId={editingGameId}
            onStartEditing={startEditing}
            onCancelEditing={cancelEditing}
            saving={gameSaving}
            approving={gameApproving}
            editError={gameEditError}
            onClearError={clearGameEditError}
            venues={venues}
            divisions={divisions}
            satisfactionScore={
              lastEvaluation?.satisfaction_score ??
              schedulerRequest.satisfaction_score
            }
            lastEvaluation={lastEvaluation}
            initialViolations={schedulerRequest.violations}
            timeSlots={timeSlots}
            constraints={constraints}
            competition={competition}
            filterContent={
              <ScheduleFilters
                divisions={filterOptions.divisions}
                teams={filterOptions.teams}
                locations={filterOptions.locations}
                surfaces={filterOptions.surfaces}
                statuses={filterOptions.statuses}
                filters={gameFilters}
                onFilterChange={setGameFilters}
              />
            }
            approvedDivisionUids={schedulerRequest.approved_division_uids}
            onSwapGames={handleSwapGamesPreview}
            onSaveCheckpoint={handleSaveCheckpoint}
            onRestoreCheckpoint={handleRestoreCheckpoint}
            isSavingCheckpoint={isSaving}
            isRestoringCheckpoint={isRestoring}
            hasChanges={schedulerRequest?.has_changes_since_checkpoint ?? false}
          />
        </>
      )
    }

    // Default: empty state (no schedule generated yet)
    return (
      <>
        <ScheduleSummary
          loading={false}
          startsAt={competition?.starts_at}
          endsAt={competition?.ends_at}
        />
        <hr
          css={css`
            border: none;
            border-top: 1px solid ${colors.SECONDARY_100};
            margin: 16px 0;
            width: 100%;
          `}
        />
        <ScheduleReviewTable divisions={[]} />
        <GeneratedGamesSection games={[]} />
      </>
    )
  }

  return (
    <App>
      <SchedulerErrorBoundary>
        <div css={pageContainerStyles}>
          {/* Header */}
          <header css={headerStyles}>
            <div css={headerInnerStyles}>
              <div css={leftSectionStyles}>
                <button css={backLinkStyles} onClick={handleBack} type='button'>
                  <Icon
                    name='chevron-left'
                    fontSize={16}
                    color={colors.NEUTRAL_50}
                  />
                  BACK
                </button>
                <div css={titleSectionStyles}>
                  {competition?.image?.full_path && (
                    <img
                      src={competition.image.full_path}
                      alt={competition?.name || 'Competition logo'}
                      css={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <h1 css={titleStyles}>
                    {competition?.name || 'New Schedule'}
                  </h1>
                </div>
              </div>

              <div css={actionButtonsStyles}>
                <button
                  css={cancelButtonStyles}
                  onClick={handleCancel}
                  type='button'
                  aria-label='Cancel schedule creation'
                >
                  Cancel
                </button>
                {(isGenerated || isPartiallyGenerated) && requestId && (
                  <button
                    css={calendarViewButtonStyles}
                    onClick={() =>
                      history.push(
                        `/scheduler-wizard/${competitionId}/configure/${requestId}/calendar`
                      )
                    }
                    type='button'
                    aria-label='Open calendar view'
                  >
                    <Icon
                      name='calendar-alt'
                      fontSize={20}
                      color={colors.WHITE}
                    />
                    Calendar View
                  </button>
                )}
                {(isGenerated || isPartiallyGenerated) && (
                  <button
                    css={publishButtonStyles}
                    onClick={handlePublishClick}
                    type='button'
                    aria-label='Publish schedule'
                  >
                    <Icon
                      name='magic'
                      fontSize={20}
                      color={colors.SECONDARY_300}
                    />
                    Publish Schedule
                  </button>
                )}
                <div
                  css={css`
                    width: 1px;
                    height: 32px;
                    background-color: ${colors.SECONDARY_100};
                  `}
                />
                <button
                  css={generateButtonStyles}
                  onClick={handleGenerate}
                  type='button'
                  aria-label='Generate schedule automatically'
                  disabled={isPolling || !canGenerate}
                  title={
                    !canGenerate && generateDisabledReason
                      ? generateDisabledReason
                      : undefined
                  }
                >
                  <Icon
                    name='magic'
                    fontSize={24}
                    color={colors.SECONDARY_300}
                  />
                  {isPolling ? 'Generating...' : 'Generate Schedule'}
                </button>
              </div>
            </div>
          </header>

          {/* Navigation Bar */}
          <SchedulerNavigationBar
            tabs={navigationTabs}
            activeTabId={activeNavTab}
            onTabChange={setActiveNavTab}
            loading={isResuming && requestLoading}
          />

          {/* Main Content Area */}
          <div css={contentContainerStyles}>
            <div css={contentInnerStyles}>
              {isResuming && requestLoading ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 60vh;
                    gap: 16px;
                  `}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined style={LOADING_SPINNER_STYLE} spin />
                    }
                  />
                  <p
                    css={css`
                      font-family: 'Inter', sans-serif;
                      font-size: 14px;
                      color: ${colors.NEUTRAL_100};
                      margin: 0;
                    `}
                  >
                    Loading schedule data...
                  </p>
                </div>
              ) : (
                <>
                  {activeNavTab === 'time-slots' && (
                    <ConfigurePage
                      competitionId={competitionId}
                      requestId={requestId}
                      onRequestCreated={handleRequestCreated}
                      createRequest={createRequest}
                      competition={competition}
                      refetchCompetition={refetchCompetition}
                      venues={venues}
                      locationsLoading={locationsLoading}
                      locationsError={locationsError}
                      refetchLocations={refetchLocations}
                      timeSlots={timeSlots}
                      timeSlotCounts={timeSlotCounts}
                      timeSlotsLoading={timeSlotsLoading}
                      createTimeSlot={createTimeSlot}
                      updateTimeSlot={updateTimeSlot}
                      deleteTimeSlot={deleteTimeSlot}
                      duplicateTimeSlot={duplicateTimeSlot}
                      getTimeSlotsForFacility={getTimeSlotsForFacility}
                      createTimeSlotForVenue={createTimeSlotForVenue}
                      updateTimeSlotBatch={updateTimeSlotBatch}
                      deleteTimeSlotBatch={deleteTimeSlotBatch}
                      rawBlackoutDates={rawBlackoutDates}
                      constraintsLoading={constraintsLoading}
                      addBlackoutDate={addBlackoutDate}
                      deleteConstraint={deleteConstraint}
                      divisions={divisions}
                    />
                  )}
                  {activeNavTab === 'divisions-teams' && (
                    <DivisionTeamsSection
                      divisions={divisions}
                      loading={divisionsLoading}
                      error={divisionsError}
                      onToggleExpanded={toggleExpanded}
                      onAddDivision={handleAddDivision}
                      onAddTeam={handleAddTeam}
                      onDeleteTeam={handleDeleteTeam}
                      onDeleteDivision={handleDeleteDivision}
                    />
                  )}
                  {activeNavTab === 'constraints' && (
                    <ConstraintsSection
                      requestId={requestId}
                      competitionId={competitionId}
                      createRequest={createRequest}
                      onRequestCreated={handleRequestCreated}
                      divisions={constraintDivisions}
                      teams={constraintTeams}
                      venues={venues}
                      constraints={constraints}
                      loading={constraintsLoading}
                      toggleEnabled={toggleEnabled}
                      updateConstraint={updateConstraint}
                      updateConstraintType={updateConstraintType}
                      reorderConstraints={reorderConstraints}
                      deleteConstraint={deleteConstraint}
                      addConstraint={addConstraint}
                    />
                  )}
                  {activeNavTab === 'review-schedule' && renderReviewContent()}
                </>
              )}
            </div>
          </div>
        </div>
      </SchedulerErrorBoundary>

      <GenerateScheduleModal
        isOpen={isGenerateModalOpen}
        onClose={handleGenerateModalClose}
        onConfirm={handleGenerateConfirm}
        onResetDivisions={handleResetDivisions}
        divisions={modalDivisions}
        previewPayload={previewPayload}
        isLoadingPreview={isLoadingPreview}
      />

      <CancelConfirmModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelGeneration}
        cancelling={isCancelling}
      />

      <PreflightValidationModal
        visible={mutationModalVisible}
        preview={mutationPreview}
        loading={mutationLoading}
        confirming={mutationConfirming}
        error={mutationError}
        onConfirm={confirmMutation}
        onCancel={cancelMutation}
        onSelectAlternative={selectAlternativeSlot}
      />

      <PublishScheduleModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onPublish={handlePublishConfirm}
        totalGames={schedulerRequest?.total_games_count ?? 0}
        divisions={divisions.map((d) => ({
          id: d.id,
          name: d.name,
          gamesCount: schedulerRequest?.division_jobs?.find(
            (j) => j.division_id === d.id
          )?.games_count,
        }))}
      />
    </App>
  )
}

export default SchedulerWizard
